// ─────────────────────────────────────────────────────────────────────────────
// ProductEditor.tsx — ContentSectionEditPageProduct
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useRef } from 'react';
import ApiPost from '../../../../../../api-controller-mod/api-serv/post-api/postApi';
import SelectFieldInline from '../../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectFieldInline';
import { ProductSection, ProductListItem, IconItem, MediaSaveCallback } from './types';
import { LabelRow, SplitRow, DraftTextField, DraftTextArea, ImageUploadField } from './components';

const ProductEditor: React.FC<{
  data:               ProductSection;
  onChange:           (v: ProductSection) => void;
  onMediaClick?:      (onSave: MediaSaveCallback) => void;
  userId:             string;
  registerMediaUsage: (mediaUuid: string) => void;
}> = ({ data, onChange, onMediaClick, userId, registerMediaUsage }) => {
  const [selectedIdx,     setSelectedIdx]     = useState(0);
  const [svcIconPreviews, setSvcIconPreviews] = useState<Record<string, string>>({});
  const svcFileRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [cardEdits,       setCardEdits]       = useState<Record<number, Partial<ProductListItem>>>({});

  const orig = useRef(data);

  const set = (p: Partial<ProductSection>) => onChange({ ...data, ...p });

  const card       = data.lists[selectedIdx];
  const editedCard = card ? { ...card, ...(cardEdits[selectedIdx] ?? {}) } : undefined;

  const setCardField = (field: keyof ProductListItem, v: unknown) => {
    const updatedEdits = {
      ...cardEdits,
      [selectedIdx]: { ...(cardEdits[selectedIdx] ?? {}), [field]: v },
    };
    setCardEdits(updatedEdits);
    const updatedLists = data.lists.map((item, i) => ({
      ...item,
      ...(updatedEdits[i] ?? {}),
    }));
    onChange({ ...data, lists: updatedLists });
  };

  const setIconLabel = (iconIdx: number, v: string) => {
    if (!editedCard) return;
    const updated = editedCard.items.map((ic, i) => i === iconIdx ? { ...ic, label: v } : ic);
    setCardField('items', updated);
  };

  const cardOptions = data.lists.map((l, i) => ({
    value: String(i),
    label: `Product / ${l.title}`,
  }));

  return (
    <>
      <LabelRow label="Main content title" required />
      <SplitRow currentText={`[ ${orig.current.label} ]`}>
        <DraftTextField placeholder="Main content title" onChange={v => set({ label: v })} />
      </SplitRow>

      <LabelRow label="Main title" required />
      <SplitRow currentText={orig.current.title}>
        <DraftTextField placeholder="Main title" onChange={v => set({ title: v })} />
      </SplitRow>

      <LabelRow label="Main description" />
      <SplitRow currentText={orig.current.description} multiline>
        <DraftTextArea placeholder="Main description" onChange={v => set({ description: v })} rows={3} />
      </SplitRow>

      <div className="scl--cms-content-edit-product-card-row">
        <span className="scl--cms-content-edit-product-label">Product</span>
        <div className="scl--cms-content-edit-product-card-row-right">
          <SelectFieldInline
            className="scl--cms-content-edit-product-card-select"
            value={String(selectedIdx)}
            options={cardOptions}
            onChange={(v: string) => setSelectedIdx(Number(v))}
          />
        </div>
      </div>
      <div className="scl--cms-content-edit-product-divider" />

      {editedCard && (
        <>
          <LabelRow label="Product title" />
          <SplitRow currentText={orig.current.lists[selectedIdx]?.title ?? ''}>
            <DraftTextField
              key={`title-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.title ?? ''}
              placeholder="Product title"
              onChange={v => setCardField('title', v)}
            />
          </SplitRow>

          <LabelRow label="Product description" />
          <SplitRow currentText={orig.current.lists[selectedIdx]?.description ?? ''} multiline>
            <DraftTextArea
              key={`desc-${selectedIdx}`}
              initialValue={cardEdits[selectedIdx]?.description ?? ''}
              placeholder="Product description"
              onChange={v => setCardField('description', v)}
              rows={3}
            />
          </SplitRow>

          <ImageUploadField
            key={`img-upload-${selectedIdx}`}
            label="Icon"
            currentSrc={editedCard.img || undefined}
            currentName={editedCard.img ? (orig.current.lists[selectedIdx]?.title ?? '') : undefined}
            onFile={async file => {
              try {
                const formData = new FormData();
                formData.append('file',        file);
                formData.append('type',        'IMAGE');
                formData.append('filename',    file.name);
                formData.append('alt',         editedCard.imgAlt ?? '');
                formData.append('description', '');

                const res          = await ApiPost(`/api/v1/media/upload?userUuid=${userId}`, formData);
                const uploadedUrl: string | undefined  = (res as any)?.data?.data?.url;
                const uploadedUuid: string | undefined = (res as any)?.data?.data?.uuid;

                if (uploadedUrl) {
                  setCardField('img', uploadedUrl);
                  if (uploadedUuid) registerMediaUsage(uploadedUuid);
                  return uploadedUrl;
                }
              } catch (err) {
                console.error('[CardImgUpload] upload failed:', err);
              }
              return undefined;
            }}
            onMediaClick={
              onMediaClick
                ? () => onMediaClick(item => { if (item) setCardField('img', item.src); })
                : undefined
            }
          />

          {editedCard.items.length > 0 && (
            <div className="scl--cms-content-edit-product-service-list">
              {editedCard.items.map((ic, j) => {
                const svcKey    = `${selectedIdx}_${j}`;
                const origLabel = orig.current.lists[selectedIdx]?.items[j]?.label;
                return (
                  <div key={ic.id} className="scl--cms-content-edit-product-service-block">
                    <div className="scl--cms-content-edit-product-service-label-row">
                      <span className="scl--cms-content-edit-product-service-name">Service{j + 1}</span>
                    </div>
                    <div className="scl--cms-content-edit-product-service-card">
                      <label
                        className="scl--cms-content-edit-product-service-avatar"
                        htmlFor={`svc-icon-${svcKey}`}
                        title="Upload icon"
                        aria-label={`Upload icon for Service ${j + 1}`}
                      >
                        {(() => {
                          const resolvedIcon =
                            svcIconPreviews[svcKey]
                            ?? (cardEdits[selectedIdx]?.items as IconItem[] | undefined)?.[j]?.Icon
                            ?? ic.Icon;
                          return resolvedIcon ? (
                            <img
                              key={resolvedIcon}
                              src={resolvedIcon}
                              alt={ic.iconAlt}
                              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : null;
                        })()}
                        <span className="scl--cms-content-edit-product-service-avatar-overlay" aria-hidden="true">
                          <svg className="scl--cms-content-edit-product-service-avatar-upload-icon" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                            <path d="M5 6.5V1M5 1L3 3M5 1L7 3" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M1.5 7.5V8.5C1.5 8.78 1.72 9 2 9H8C8.28 9 8.5 8.78 8.5 8.5V7.5" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round"/>
                          </svg>
                        </span>
                        <input
                          id={`svc-icon-${svcKey}`}
                          ref={el => { svcFileRefs.current[svcKey] = el; }}
                          type="file"
                          accept=".jpg,.jpeg,.png,.svg"
                          className="scl--cms-content-edit-product-service-avatar-input"
                          onChange={async e => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            e.target.value = '';

                            const blobUrl = URL.createObjectURL(file);
                            setSvcIconPreviews(prev => ({ ...prev, [svcKey]: blobUrl }));

                            try {
                              const formData = new FormData();
                              formData.append('file',        file);
                              formData.append('type',        'IMAGE');
                              formData.append('filename',    file.name);
                              formData.append('alt',         ic.iconAlt ?? '');
                              formData.append('description', '');

                              const res          = await ApiPost(`/api/v1/media/upload?userUuid=${userId}`, formData);
                              const uploadedUrl: string | undefined  = (res as any)?.data?.data?.url;
                              const uploadedUuid: string | undefined = (res as any)?.data?.data?.uuid;

                              if (!uploadedUrl) {
                                console.error('[SvcIconUpload] no url in response');
                                return;
                              }

                              setSvcIconPreviews(prev => ({ ...prev, [svcKey]: uploadedUrl }));

                              if (editedCard) {
                                const updatedItems = editedCard.items.map((item, i) =>
                                  i === j ? { ...item, Icon: uploadedUrl } : item
                                );
                                setCardField('items', updatedItems);
                              }

                              if (uploadedUuid) registerMediaUsage(uploadedUuid);
                            } catch (err) {
                              console.error('[SvcIconUpload] upload failed:', err);
                            }
                          }}
                        />
                      </label>
                      <div className="scl--cms-content-edit-product-service-meta">
                        <span className="scl--cms-content-edit-product-service-icon-name">
                          {origLabel || `Service ${j + 1}`}
                        </span>
                      </div>
                      <div className="scl--cms-content-edit-product-service-input">
                        <DraftTextField
                          key={`svc-label-${selectedIdx}-${j}`}
                          initialValue={(cardEdits[selectedIdx]?.items as IconItem[] | undefined)?.[j]?.label ?? ic.label}
                          placeholder="Service label"
                          onChange={v => setIconLabel(j, v)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ProductEditor;